import type { CodeContent } from '@/types/github';

import { isTestFile } from '@/lib/file-paths/is-test-file';

type SymbolDefinitionResult = {
  filename: string;
  lineNumber: number;
  side: 'new' | 'old';
};

// Matches common definition keywords across languages (TS/JS/Python/Go/Rust/Java/C#/Ruby).
// The pattern expects optional leading whitespace, optional export/pub/public modifiers,
// a definition keyword, then the symbol name.
function buildDefinitionPattern(symbolName: string): RegExp {
  const escaped = symbolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Keyword-based definitions:
  //   function X, async function X, const X, let X, var X,
  //   class X, interface X, type X, enum X, struct X, trait X,
  //   def X, func X, fn X, module X, object X
  const keywords =
    '(?:async\\s+)?(?:function\\*?|const|let|var|class|interface|type|enum|struct|trait|def|func|fn|module|object)';

  // Optional modifiers before the keyword: export, export default, pub, public, private, protected, static, abstract
  const modifiers =
    '(?:(?:export\\s+(?:default\\s+)?|pub(?:\\(crate\\))?\\s+|public\\s+|private\\s+|protected\\s+|static\\s+|abstract\\s+)*)';

  return new RegExp(`^[\\t ]*${modifiers}${keywords}\\s+${escaped}(?![\\p{L}\\p{N}_$])`, 'mu');
}

// A type's `<...>` arguments, one level deep, so a type default such as `<P = {}>` inside an
// annotation is not read as the assignment's `=`.
const TYPE_ARGUMENTS = '<(?:[^<>\\r\\n]|<[^<>\\r\\n]*>)*>';

// Matches assignment-style definitions: symbolName = or symbolName: Type =
// at a reasonable indentation level (allows some indentation for class members).
// Does NOT match bare function calls like symbolName() -- those are covered by
// the keyword pattern (function/def/func/fn).
function buildAssignmentPattern(symbolName: string): RegExp {
  const escaped = symbolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return new RegExp(
    `^[\\t ]*(?:(?:export\\s+(?:default\\s+)?|pub(?:\\(crate\\))?\\s+|public\\s+|private\\s+|protected\\s+|static\\s+|abstract\\s+|readonly\\s+)*)?${escaped}[\\t ]*(?::(?:=>|${TYPE_ARGUMENTS}|[^=;<\\r\\n])+)?=(?!=|>)`,
    'm',
  );
}

function getLineNumber(content: string, charIndex: number): number {
  let line = 1;
  for (let i = 0; i < charIndex && i < content.length; i++) {
    if (content[i] === '\n') {
      line++;
    }
  }
  return line;
}

function isPlausibleSymbol(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > 80) {
    return false;
  }

  // Exclude file paths (contain / or backslash)
  if (trimmed.includes('/') || trimmed.includes('\\')) {
    return false;
  }

  // Exclude things that look like file extensions (e.g. "config.ts", "style.css")
  if (
    /^\S+\.\w{1,5}$/.test(trimmed) &&
    /\.(?:tsx?|jsx?|css|html|json|md|py|go|rs|java|rb|ya?ml|toml|xml|sql|sh|bash|[chrm]|cpp|hpp|cs|swift|kt|scala|exs?|erl|clj|dart|lua|php|pl)$/i.test(
      trimmed,
    )
  ) {
    return false;
  }

  // Exclude multi-line content
  if (trimmed.includes('\n')) {
    return false;
  }

  // Exclude shell commands or sentences (spaces with no dots)
  if (/\s/.test(trimmed) && !/^[\w.]+$/.test(trimmed)) {
    return false;
  }

  return true;
}

// Detects import/require statements across languages so we can skip them in fallback search.
const IMPORT_LINE_PATTERN =
  /^[\t ]*(?:import\s|from\s+\S+\s+import\s|(?:const|let|var)\s.*=\s*require\s*\(|require\s*\(|use\s+[\w:]+;)/;

function isImportLine(line: string): boolean {
  return IMPORT_LINE_PATTERN.test(line);
}

function getLineAt(content: string, charIndex: number): string {
  const lineStart = content.lastIndexOf('\n', charIndex - 1) + 1;
  const lineEnd = content.indexOf('\n', charIndex);
  return content.substring(lineStart, lineEnd === -1 ? content.length : lineEnd);
}

/**
 * The 1-based line that defines `name` in `content`: a keyword definition first, then an
 * assignment. Null when the content only uses the name.
 */
export function findSymbolDefinitionLine(content: string, name: string): number | null {
  const match =
    buildDefinitionPattern(name).exec(content) ?? buildAssignmentPattern(name).exec(content);

  return match ? getLineNumber(content, match.index) : null;
}

function searchInContent(
  symbolName: string,
  content: string,
  side: 'new' | 'old',
): { lineNumber: number; side: 'new' | 'old'; isDefinition: boolean } | null {
  const lineNumber = findSymbolDefinitionLine(content, symbolName);

  return lineNumber === null ? null : { lineNumber, side, isDefinition: true };
}

// Searches for the first non-import occurrence of a symbol in file content.
function searchFallbackOccurrence(
  symbolName: string,
  content: string,
  side: 'new' | 'old',
): { lineNumber: number; side: 'new' | 'old'; isDefinition: boolean } | null {
  const escaped = symbolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const wordBoundaryPattern = new RegExp(`\\b${escaped}\\b`, 'g');

  for (
    let match = wordBoundaryPattern.exec(content);
    match !== null;
    match = wordBoundaryPattern.exec(content)
  ) {
    const line = getLineAt(content, match.index);
    if (!isImportLine(line)) {
      return {
        lineNumber: getLineNumber(content, match.index),
        side,
        isDefinition: false,
      };
    }
  }
  return null;
}

// Hunk header format: @@ -oldStart[,oldCount] +newStart[,newCount] @@
const HUNK_HEADER_PATTERN = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

type PatchSearchResult = { lineNumber: number; side: 'new' | 'old' };

// Searches the unified diff patch for the most relevant line mentioning a symbol.
// Prioritises added lines (+) over context lines over removed lines (-).
function searchPatchForSymbol(symbolName: string, patch: string): PatchSearchResult | null {
  const escaped = symbolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const wordPattern = new RegExp(`\\b${escaped}\\b`);

  const lines = patch.split('\n');

  let newLineNumber = 0;
  let oldLineNumber = 0;

  let bestAdded: PatchSearchResult | null = null;
  let bestContext: PatchSearchResult | null = null;
  let bestRemoved: PatchSearchResult | null = null;

  for (const line of lines) {
    const hunkMatch = HUNK_HEADER_PATTERN.exec(line);
    if (hunkMatch?.[1] && hunkMatch[2]) {
      oldLineNumber = Number.parseInt(hunkMatch[1], 10);
      newLineNumber = Number.parseInt(hunkMatch[2], 10);
      continue;
    }

    // Skip lines outside hunks (diff headers like "diff --git", "index", "---", "+++")
    if (newLineNumber === 0 && oldLineNumber === 0) {
      continue;
    }

    const lineContent = line.substring(1); // strip the +/-/space prefix
    if (!wordPattern.test(lineContent) || isImportLine(lineContent)) {
      // Still track line numbers even if no match
      if (line.startsWith('+')) {
        newLineNumber++;
      } else if (line.startsWith('-')) {
        oldLineNumber++;
      } else {
        newLineNumber++;
        oldLineNumber++;
      }
      continue;
    }

    // Symbol found on this line -- record it by priority
    if (line.startsWith('+') && !bestAdded) {
      bestAdded = { lineNumber: newLineNumber, side: 'new' };
    } else if (line.startsWith('-') && !bestRemoved) {
      bestRemoved = { lineNumber: oldLineNumber, side: 'old' };
    } else if (!line.startsWith('+') && !line.startsWith('-') && !bestContext) {
      bestContext = { lineNumber: newLineNumber, side: 'new' };
    }

    // Advance line counters
    if (line.startsWith('+')) {
      newLineNumber++;
    } else if (line.startsWith('-')) {
      oldLineNumber++;
    } else {
      newLineNumber++;
      oldLineNumber++;
    }

    // Short-circuit: added line is highest priority
    if (bestAdded) {
      return bestAdded;
    }
  }

  return bestAdded ?? bestContext ?? bestRemoved ?? null;
}

function searchFileInPatch(symbolName: string, file: CodeContent): PatchSearchResult | null {
  if (!file.patch) {
    return null;
  }
  return searchPatchForSymbol(symbolName, file.patch);
}

function searchFileForDefinition(
  symbolName: string,
  file: CodeContent,
): { lineNumber: number; side: 'new' | 'old'; isDefinition: boolean } | null {
  // Prefer definitions in new content (current version of the file)
  if (file.newContent) {
    const result = searchInContent(symbolName, file.newContent, 'new');
    if (result) {
      return result;
    }
  }

  if (file.oldContent) {
    const result = searchInContent(symbolName, file.oldContent, 'old');
    if (result) {
      return result;
    }
  }

  return null;
}

function searchFileForOccurrence(
  symbolName: string,
  file: CodeContent,
): { lineNumber: number; side: 'new' | 'old'; isDefinition: boolean } | null {
  if (file.newContent) {
    const result = searchFallbackOccurrence(symbolName, file.newContent, 'new');
    if (result) {
      return result;
    }
  }

  if (file.oldContent) {
    const result = searchFallbackOccurrence(symbolName, file.oldContent, 'old');
    if (result) {
      return result;
    }
  }

  return null;
}

function toResult(
  file: CodeContent,
  r: { lineNumber: number; side: 'new' | 'old' },
): SymbolDefinitionResult {
  return { filename: file.filename, lineNumber: r.lineNumber, side: r.side };
}

/**
 * The first of `files` that defines `symbolName`. Every new content comes before any old one, so a
 * definition the PR moved between files opens where it now lives, and test files come last.
 * Unlike `findSymbolDefinition`, a file that only uses the name never matches.
 */
export function findSymbolDefinitionInFiles<
  TFile extends Pick<CodeContent, 'filename' | 'newContent' | 'oldContent'>,
>(
  symbolName: string,
  files: readonly TFile[],
): { file: TFile; lineNumber: number; side: 'new' | 'old' } | null {
  const groups = [
    files.filter((file) => !isTestFile(file.filename)),
    files.filter((file) => isTestFile(file.filename)),
  ];
  const revisions = groups.flatMap((group) => [
    ...group.map((file) => ({ file, side: 'new' as const, content: file.newContent })),
    ...group.map((file) => ({ file, side: 'old' as const, content: file.oldContent })),
  ]);

  for (const { file, side, content } of revisions) {
    const lineNumber = content ? findSymbolDefinitionLine(content, symbolName) : null;

    if (lineNumber !== null) {
      return { file, lineNumber, side };
    }
  }

  return null;
}

/**
 * Finds the definition of a symbol across the PR's changed files.
 *
 * Search strategy (test files are deprioritised to the end):
 * 1-2. Definition patterns (keyword/assignment) in preferred, then all non-test files
 * 3-4. Patch search (changed lines mentioning symbol) in preferred, then all non-test files
 * 5-6. Non-import occurrence in full content, preferred then all non-test files
 * 7.   Last resort: test files (definitions, then patch, then occurrences)
 */
export function findSymbolDefinition(
  symbolName: string,
  files: CodeContent[],
  preferredFiles?: CodeContent[],
): SymbolDefinitionResult | null {
  if (!isPlausibleSymbol(symbolName)) {
    return null;
  }

  const trimmedSymbol = symbolName.trim();

  const preferred = preferredFiles ?? [];
  const preferredNonTest = preferred.filter((f) => !isTestFile(f.filename));
  const nonTestFiles = files.filter((f) => !isTestFile(f.filename));
  const testFiles = files.filter((f) => isTestFile(f.filename));

  // Phase 1: Search preferred non-test files for definitions
  for (const file of preferredNonTest) {
    const result = searchFileForDefinition(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 2: Search all non-test files for definitions
  for (const file of nonTestFiles) {
    const result = searchFileForDefinition(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 3: Search preferred non-test files in patch (changed lines)
  for (const file of preferredNonTest) {
    const result = searchFileInPatch(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 4: Search all non-test files in patch (changed lines)
  for (const file of nonTestFiles) {
    const result = searchFileInPatch(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 5: Fallback to first non-import occurrence in preferred non-test files
  for (const file of preferredNonTest) {
    const result = searchFileForOccurrence(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 6: Fallback to first non-import occurrence in all non-test files
  for (const file of nonTestFiles) {
    const result = searchFileForOccurrence(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  // Phase 7: Last resort -- search test files (definitions, patch, then occurrences)
  for (const file of testFiles) {
    const result = searchFileForDefinition(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  for (const file of testFiles) {
    const result = searchFileInPatch(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  for (const file of testFiles) {
    const result = searchFileForOccurrence(trimmedSymbol, file);
    if (result) {
      return toResult(file, result);
    }
  }

  return null;
}

export function findMatchingFilename(text: string, files: CodeContent[]): string | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  const exactMatch = files.find((f) => f.filename === trimmed);
  if (exactMatch) {
    return exactMatch.filename;
  }

  const suffixMatches = files.filter((f) => f.filename.endsWith(`/${trimmed}`));
  if (suffixMatches.length === 1 && suffixMatches[0]) {
    return suffixMatches[0].filename;
  }

  return null;
}

export { isPlausibleSymbol, isTestFile };
export type { SymbolDefinitionResult };
